import React, { useState, useEffect, useRef } from 'react';
import { ENV, SITE_URL } from '../../../config/index.js';

// ux
import LoadingAnimation from '../ux/LoadingAnimation';
import LoadingAnimationSimple from '../ux/LoadingAnimationSimple';
import FuseHelpModal from '../ux/FuseHelpModal.js';
import ProgressBar from '../ux/ProgressBar.js';

// Enhanced Markdown parser function
function parseMarkdown(text) {
  // Replace bold text (**text**) with <strong>text</strong>
  let html = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

  // Use a regular expression to split the text into parts at each '- '
  let parts = html.split(/(?=- )/g);

  // Initialize variables
  let parsedLines = [];
  let inList = false;

  parts.forEach((part) => {
    let trimmedPart = part.trim();

    if (trimmedPart.startsWith('- ')) {
      // Handle list items
      if (!inList) {
        inList = true;
        parsedLines.push('<ul>');
      }
      let listItemContent = trimmedPart.substring(2).trim();
      parsedLines.push('<li>' + listItemContent + '</li>');
    } else {
      // Handle paragraphs
      if (inList) {
        inList = false;
        parsedLines.push('</ul>');
      }
      if (trimmedPart !== '') {
        parsedLines.push('<p>' + trimmedPart + '</p>');
      }
    }
  });

  // Close any unclosed lists
  if (inList) {
    parsedLines.push('</ul>');
    inList = false;
  }

  // Join the parsed lines into the final HTML
  html = parsedLines.join('\n');

  return html;
}

const Project = ({
  inputs,
  projectThread,
  projectFuseList,
  projectConvo,
  projectLoading,
  updatingProjectList,
  handleInputsChange,
  handleUpdateProjectConvo,
  loadingMessage,
  handleCreateFuse,
  fuseLoadingList,
  fetchFuse,
  createPitch,
  loadingPitchesArray,
  deckLink,
  helpOpen,
  toggleHelp
}) => {
  const [createPitchOpen, setCreatePitchOpen] = useState(false);
  const [selectedFuses, setSelectedFuses] = useState([]);
  const [loadingPitch, setLoadingPitch] = useState(false);
  const [pitchLink, setPitchLink] = useState('');
  const [backInfo, setBackInfo] = useState({
    ID: null
  });
  const [activeProgressBars, setActiveProgressBars] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  // Toggle state for light/dark mode
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Toggle handler
  const handleToggleChange = () => {
    setIsDarkMode(!isDarkMode);
  };

  // console log dark mode state
  useEffect(() => {
    console.log('Dark mode is:', isDarkMode ? 'on' : 'off');
  }, [isDarkMode]);

  // maintain loading states while navigating
  useEffect(() => {
    if (inputs.project_id && fuseLoadingList.length > 0) {
      console.log('fuseLoadingList:', fuseLoadingList);
      const initialProgress = {};
      fuseLoadingList.forEach((fuse) => {
        const uniqueId = fuse.idea_id;
        initialProgress[uniqueId] = true;
      });
      setActiveProgressBars(initialProgress);
    }
  }, [inputs.project_id, fuseLoadingList]);

  const maxSelection = 3;

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    setPitchLink(deckLink);
  }, [deckLink]);

  useEffect(() => {
    scrollToBottom();
  }, [projectConvo]);

  let ideaIndex = 0;

  const handleItemClick = (fuse) => {
    const isSelected = selectedFuses.includes(fuse);

    if (isSelected) {
      // Remove fuse if it's already selected
      setSelectedFuses((prevFuses) =>
        prevFuses.filter((item) => item !== fuse)
      );
    } else if (selectedFuses.length < maxSelection) {
      // Add fuse if it's not already selected and under the limit
      setSelectedFuses((prevFuses) => [...prevFuses, fuse]);
    }
  };

  const openCreatePitch = () => {
    setCreatePitchOpen(true);
  };

  const closeCreatePitch = () => {
    setCreatePitchOpen(false);
    setSelectedFuses([]);
  };

  const handleCreatePitch = () => {
    createPitch(
      inputs.project_id,
      inputs.brand_name,
      selectedFuses,
      isDarkMode
    );
    closeCreatePitch();
  };

  const handleStartProgressBar = (uniqueId) => {
    setActiveProgressBars((prevState) => ({
      ...prevState,
      [uniqueId]: true
    }));
  };

  const handleCompleteProgressBar = (uniqueId) => {
    setActiveProgressBars((prevState) => ({
      ...prevState,
      [uniqueId]: false
    }));
  };

  // if inputs.project_id is in the loadingPitchesArray, set loadingPitch to true
  useEffect(() => {
    if (loadingPitchesArray.includes(inputs.project_id)) {
      setLoadingPitch(true);
    } else {
      setLoadingPitch(false);
    }
  }, [loadingPitchesArray, inputs.project_id]);

  // setbackInfo
  useEffect(() => {
    setBackInfo({
      ID: inputs.project_id
    });
  }, [inputs.project_id]);

  // tutorial functions
  const totalSteps = 3;
  const nextHelpStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prevStep) => prevStep + 1);
    } else {
      endHelpTutorial();
    }
  };

  const prevHelpStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => prevStep - 1);
    }
  };

  const endHelpTutorial = () => {
    toggleHelp();
    setCurrentStep(1);
  };

  return (
    <div className="fuselight-project-container">
      <div className="launchpad-bg">
        <div></div>
        <div></div>
        <div></div>
      </div>
      {projectLoading ? (
        <div className="flex-loading-container">
          <LoadingAnimation label="Loading Launchplan" />
        </div>
      ) : (
        <>
          {helpOpen && (
            <div className={`project-help-modal-container step-${currentStep}`}>
              <div className="project-interface">
                <FuseHelpModal
                  classes="fuse-help-modal help-modal-1"
                  clickClose={endHelpTutorial}
                  clickNext={nextHelpStep}
                  clickPrev={prevHelpStep}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                >
                  <div className="mobile-help-content">
                    <button
                      disabled
                      className="btn btn--primary-outline tutorial-button"
                    >
                      Ignite{' '}
                      <img
                        src={
                          ENV === 'development'
                            ? `${SITE_URL}/app/themes/hahn/images/logo/FuseLightLogo.svg`
                            : `${SITE_URL}/wp-content/themes/hahn/images/logo/FuseLightLogo.svg`
                        }
                        alt="Fuselight Logo Icon"
                        className="header-logo-icon"
                        width={30}
                      />
                    </button>
                    <p>Click Ignite to evaluate the idea you want.</p>
                    <div className="chat-demo">
                      <div className="blue-line"></div>
                      <button disabled>
                        <i className="far fa-paper-plane"></i>
                      </button>
                    </div>
                    <p>
                      Use the chat feature to discuss, clarify, revise or add
                      more ideas to the mix.
                    </p>
                  </div>
                  <p className="desktop-help-content">
                    Here are the fusion ideas.
                    <br /> Click{' '}
                    <strong style={{ color: '#00edff' }}>Ignite</strong> to
                    evaluate the idea you want.
                  </p>
                </FuseHelpModal>
                <FuseHelpModal
                  classes="fuse-help-modal help-modal-2"
                  clickClose={endHelpTutorial}
                  clickNext={nextHelpStep}
                  clickPrev={prevHelpStep}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                >
                  <p>
                    Use the chat feature to discuss, clarify, revise or add more
                    ideas to the mix.{' '}
                  </p>
                </FuseHelpModal>
              </div>
              <div className="project-sidebar">
                <FuseHelpModal
                  classes="fuse-help-modal help-modal-3"
                  clickClose={endHelpTutorial}
                  clickNext={nextHelpStep}
                  clickPrev={prevHelpStep}
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                >
                  <p>
                    Once you have one or more fusions created, you will get the
                    option to{' '}
                    <strong style={{ color: '#ff8b00' }}>Create pitch</strong>.
                    Choose up to three fusions to feature in an automatically
                    generated, fully editable pitch deck.
                  </p>
                </FuseHelpModal>
              </div>
            </div>
          )}
          <div className="project-interface">
            <div className="project-conversation">
              {projectConvo.messages &&
                projectConvo.messages.map((message, messageIndex) => {
                  const isMessageObject =
                    typeof message === 'object' && message !== null;
                  const hasOpeningText =
                    isMessageObject && message.opening_text;
                  const hasIdeas =
                    isMessageObject &&
                    message.idea &&
                    Array.isArray(message.idea) &&
                    message.idea.length > 0;

                  // Determine if opening_text contains Markdown that needs parsing
                  const needsParsing =
                    hasOpeningText &&
                    (message.opening_text.includes('- ') ||
                      message.opening_text.includes('**'));

                  // Parse opening_text if it needs parsing
                  const renderedOpeningText = needsParsing
                    ? parseMarkdown(message.opening_text)
                    : message.opening_text;

                  return (
                    <div
                      className={`conversation-message ${message.sender}`}
                      key={messageIndex}
                    >
                      {hasOpeningText ? (
                        <>
                          {needsParsing ? (
                            // Render parsed HTML using dangerouslySetInnerHTML
                            <div
                              className={
                                hasIdeas ? 'response' : 'response no-ideas'
                              }
                              dangerouslySetInnerHTML={{
                                __html: renderedOpeningText
                              }}
                            ></div>
                          ) : (
                            // Render as plain text
                            <h2
                              className={
                                hasIdeas ? 'response' : 'response no-ideas'
                              }
                            >
                              {renderedOpeningText}
                            </h2>
                          )}
                          {hasIdeas &&
                            message.idea.map((idea) => {
                              ideaIndex++;
                              const uniqueId =
                                idea.idea_id ||
                                `${inputs.project_id}-${ideaIndex}`;

                              const matchingFuse = projectFuseList.find(
                                (fuse) => fuse.idea_id === uniqueId
                              );
                              const isFuseLoaded =
                                matchingFuse &&
                                !fuseLoadingList.some(
                                  (fuseLoading) =>
                                    fuseLoading.project_id ===
                                      inputs.project_id &&
                                    fuseLoading.idea_id === uniqueId
                                );
                              return (
                                <div
                                  className={
                                    fuseLoadingList.some(
                                      (fuseLoading) =>
                                        fuseLoading.project_id ===
                                          inputs.project_id &&
                                        fuseLoading.idea_id === uniqueId
                                    )
                                      ? 'fuse-idea idea-loading-animation'
                                      : 'fuse-idea'
                                  }
                                  key={uniqueId}
                                >
                                  <div className="idea-content">
                                    <h3>{idea.title}</h3>
                                    <p>{idea.desc}</p>
                                    <div className="loading-animation-container">
                                      {projectFuseList.some(
                                        (fuse) =>
                                          fuse.idea_id === uniqueId &&
                                          !fuseLoadingList.some(
                                            (fuseLoading) =>
                                              fuseLoading.project_id ===
                                                inputs.project_id &&
                                              fuseLoading.idea_id === uniqueId
                                          )
                                      ) ? (
                                        <button
                                          className="btn btn--primary view-button"
                                          onClick={() =>
                                            fetchFuse(
                                              projectFuseList.find(
                                                (fuse) =>
                                                  fuse.idea_id === uniqueId
                                              ).ID,
                                              backInfo
                                            )
                                          }
                                        >
                                          View
                                        </button>
                                      ) : (
                                        <>
                                          <button
                                            className="btn btn--primary-outline ignite-button"
                                            onClick={() => {
                                              handleStartProgressBar(uniqueId); // Start ProgressBar
                                              handleCreateFuse(idea, uniqueId);
                                            }}
                                            disabled={updatingProjectList}
                                          >
                                            Ignite
                                            <img
                                              src={
                                                ENV === 'development'
                                                  ? `${SITE_URL}/app/themes/hahn/images/logo/FuseLightLogo.svg`
                                                  : `${SITE_URL}/wp-content/themes/hahn/images/logo/FuseLightLogo.svg`
                                              }
                                              alt="Fuselight Logo Icon"
                                              className="header-logo-icon"
                                              width={30}
                                            />
                                          </button>
                                          <div className="loading-sequence">
                                            <ProgressBar
                                              brand={inputs.brand_name}
                                              partner={idea.partner}
                                              isActive={
                                                activeProgressBars[uniqueId]
                                              }
                                              hasLoaded={isFuseLoaded}
                                              onComplete={() =>
                                                handleCompleteProgressBar(
                                                  uniqueId
                                                )
                                              }
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </>
                      ) : isMessageObject && message.sender ? (
                        // Handle messages without opening_text but with sender (e.g., user messages)
                        <h2 className={`response ${message.sender}`}>
                          <strong>{message.sender}:</strong>{' '}
                          {message.opening_text || ''}
                        </h2>
                      ) : (
                        // Handle messages that are strings or unrecognized objects
                        <h2 className="response">{message.toString()}</h2>
                      )}
                    </div>
                  );
                })}

              {loadingMessage && <LoadingAnimationSimple />}
              <div
                style={{ float: 'left', clear: 'both' }}
                ref={messagesEndRef}
              ></div>
            </div>
            <div className="message-input-container">
              <input
                type="text"
                name="message"
                placeholder="I like the first idea but give me two new ones"
                value={inputs.message}
                onChange={handleInputsChange}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    if (inputs.message.trim() !== '') {
                      handleUpdateProjectConvo(projectThread);
                    }
                  }
                }}
              />
              <button
                onClick={() => {
                  if (inputs.message.trim() !== '') {
                    handleUpdateProjectConvo(projectThread);
                  }
                }}
                disabled={inputs.message.trim() === ''}
              >
                <i className="far fa-paper-plane"></i>
              </button>
            </div>
          </div>
          <div className="project-sidebar">
            <div className="project-notes">
              {inputs.brand_name && <p>{inputs.brand_name}</p>}
              {inputs.celeb_name && <p>{inputs.celeb_name}</p>}
              {inputs.context && <p>{inputs.context}</p>}
              {inputs.idea && <p>{inputs.idea}</p>}
            </div>
            <div
              className={
                createPitchOpen ? 'fuse-list create-pitch-open' : 'fuse-list'
              }
            >
              {createPitchOpen && (
                <button
                  className="create-pitch-close"
                  onClick={closeCreatePitch}
                ></button>
              )}
              {createPitchOpen && (
                <p>Select up to 3 fusions to add to the pitch</p>
              )}

              {projectFuseList.map((fuse, fuseIndex) => {
                return (
                  <div
                    className={
                      selectedFuses.includes(fuse) ? 'fuse selected' : 'fuse'
                    }
                    key={fuseIndex}
                    onClick={
                      createPitchOpen
                        ? () => handleItemClick(fuse)
                        : () => fetchFuse(fuse.ID, backInfo)
                    }
                  >
                    <div className="fuse-content">
                      {fuse.fuse_title ? (
                        <h3>{fuse.fuse_title}</h3>
                      ) : (
                        <h3>
                          {fuse.brand_name} x {fuse.celebrity_name}
                        </h3>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {loadingPitch ? (
              <div className="pitch-buttons">
                <LoadingAnimationSimple />
              </div>
            ) : (
              <>
                {projectFuseList.length > 0 ? (
                  <div className="pitch-buttons">
                    {createPitchOpen ? (
                      <>
                        {selectedFuses.length > 0 && (
                          <>
                            <label className="toggle-container">
                              <input
                                type="checkbox"
                                checked={isDarkMode}
                                onChange={handleToggleChange}
                              />
                              <span className="slider"></span>
                              <span className="mode-label">
                                {isDarkMode ? 'Dark' : 'Light'}
                              </span>
                            </label>
                            <button
                              className="btn btn--secondary-outline"
                              onClick={handleCreatePitch}
                              disabled={fuseLoadingList.length > 0}
                            >
                              Create
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {pitchLink && (
                          <a
                            className="btn btn--primary-outline"
                            href={pitchLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View pitch
                          </a>
                        )}
                        <button
                          className="btn btn--secondary-outline"
                          onClick={openCreatePitch}
                          disabled={fuseLoadingList.length > 0}
                        >
                          Create pitch
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="pitch-buttons">
                    <button className="btn btn--secondary-outline" disabled>
                      Create pitch
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Project;
